import './App.css'

import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FixedSizeList } from 'react-window'
import PDFViewer from './PDFViewer'
import LazyImage from './LazyImage'

interface Album {
  id: string
  name: string
  description: string
  coverImage: string
  thumbnailSrcSet?: string
  thumbnailWebpSrcSet?: string
  photoCount: number
  createdDate: string
  hidden?: boolean
}

interface Photo {
  id: string
  albumId: string
  title: string
  url: string
  thumbnailUrl?: string
  thumbnailSrcSet?: string
  thumbnailWebpSrcSet?: string
  caption: string
  uploadDate: string
  type?: 'image' | 'video' | 'pdf'
}

interface Media {
  url: string
  type?: 'image' | 'video'
  focalPoint?: string | null
}

interface HomeProps {
  albums: Album[]
  photos: Photo[]
}

// Fallback featured photos if backend unavailable
const FALLBACK_FEATURED_MEDIA: Media[] = [
  { url: 'https://bird-created.s3.us-east-1.amazonaws.com/eqsong-images/_DSC2154-Recovered+copy.jpg', type: 'image' },
  { url: 'https://bird-created.s3.us-east-1.amazonaws.com/eqsong-images/blog-cover-woodpecker.jpg', type: 'image' },
  { url: 'https://bird-created.s3.us-east-1.amazonaws.com/eqsong-images/blog-cover-eagles.jpg', type: 'image' }
]

// backend URL logic is unchanged
const BACKEND_URL = (typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : 'https://8vosl9zwo9.execute-api.us-east-1.amazonaws.com')

function Home ({ albums, photos }: HomeProps): JSX.Element {
  const location = useLocation()
  const [heroMedia, setHeroMedia] = useState<Media[]>(FALLBACK_FEATURED_MEDIA)
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0)
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null)
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null)
  const [fullscreenOpen, setFullscreenOpen] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(100)
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [reviewTimes, setReviewTimes] = useState<Record<string, number>>({})
  const [reviewCounts, setReviewCounts] = useState<Record<string, number>>({})
  const [openedAt, setOpenedAt] = useState<number | null>(null)
  const [openedPhoto, setOpenedPhoto] = useState<{ albumId: string, filename: string } | null>(null)
  const [heroHeight, setHeroHeight] = useState(700)

  // Create virtual "All Photo View" album
  const createAllPhotosAlbum = (): Album => {
    const visibleAlbums = albums.filter(a => !a.hidden)
    // Count only photos NOT in hidden albums (eqsong-images, images, thumbnails)
    const hiddenAlbumIds = ['eqsong-images', 'images', 'thumbnails']
    const allPhotosCount = photos.filter(photo => !hiddenAlbumIds.includes(photo.albumId)).length
    // Use first available photo as cover image
    const coverPhoto = photos.find(p => p.type !== 'video' && p.type !== 'pdf') || photos[0]
    
    return {
      id: '__all_photos__',
      name: 'All Photo View',
      description: 'View all photos from all albums in one place',
      coverImage: coverPhoto?.thumbnailUrl || coverPhoto?.url || 'https://via.placeholder.com/300',
      photoCount: allPhotosCount,
      createdDate: new Date().toISOString().split('T')[0],
      hidden: false
    }
  }

  // The Home route ("/") stays mounted while browsing albums/photos, so clicking the
  // "Home" nav link (Link to="/") wouldn't otherwise reset this in-page view. React
  // Router still assigns a fresh location.key on each navigation, even to the same
  // path, so use it to detect the click and return to the main gallery.
  useEffect(() => {
    setSelectedAlbum(null)
    setSelectedPhoto(null)
    setFullscreenOpen(false)
  }, [location.key])

  // Load featured photos from backend
  useEffect(() => {
    const loadFeaturedPhotos = async (): Promise<void> => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/photos/featured-photos`)
        if (response.ok) {
          const data = await response.json()
          const photos = data.photos || []
          const media = photos.map((p: any) => ({
            url: p.url,
            type: p.type,
            focalPoint: p.focalPoint
          }))
          if (media.length > 0) {
            setHeroMedia(media)
          } else {
            setHeroMedia(FALLBACK_FEATURED_MEDIA)
          }
        } else {
          setHeroMedia(FALLBACK_FEATURED_MEDIA)
        }
      } catch (err) {
        console.log('Could not load featured photos from backend, using fallback')
        setHeroMedia(FALLBACK_FEATURED_MEDIA)
      }
    }
    loadFeaturedPhotos()
  }, [])

  // Load hero section height setting from backend
  useEffect(() => {
    const loadHeroSettings = async (): Promise<void> => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/settings/hero`)
        if (response.ok) {
          const data = await response.json()
          if (data.heroHeight) {
            setHeroHeight(data.heroHeight)
          }
        }
      } catch (err) {
        console.log('Could not load hero settings from backend, using default height')
      }
    }
    loadHeroSettings()
  }, [])

  // Auto-rotate hero media
  useEffect(() => {
    if (heroMedia.length === 0) return
    
    const interval = setInterval(() => {
      setCurrentMediaIndex((prev) => (prev + 1) % heroMedia.length)
    }, 4000) // 4 seconds for faster rotation
    
    return () => clearInterval(interval)
  }, [heroMedia.length])

  // Load review times and counts for current album
  useEffect(() => {
    const loadReviewTimes = async (): Promise<void> => {
      try {
        if (!selectedAlbum) return
        const resp = await fetch(`${BACKEND_URL}/api/photos/${selectedAlbum.id}/review-times`)
        if (resp.ok) {
          const data = await resp.json()
          setReviewTimes(data.times || {})
        }
      } catch (err) {
        // ignore
      }
    }
    const loadReviewCounts = async (): Promise<void> => {
      try {
        if (!selectedAlbum) return
        const resp = await fetch(`${BACKEND_URL}/api/photos/${selectedAlbum.id}/review-counts`)
        if (resp.ok) {
          const data = await resp.json()
          setReviewCounts(data.counts || {})
        }
      } catch (err) {
        // ignore
      }
    }
    loadReviewTimes()
    loadReviewCounts()
  }, [selectedAlbum])

  const albumGridRef = useRef<HTMLDivElement | null>(null)
  const [albumGridWidth, setAlbumGridWidth] = useState(0)
  const [albumGridHeight, setAlbumGridHeight] = useState(0)
  const MIN_COLUMN_WIDTH = 280
  const ROW_HEIGHT = 320

  useEffect(() => {
    const updateGridSize = (): void => {
      if (!albumGridRef.current) {
        setAlbumGridWidth(window.innerWidth)
        setAlbumGridHeight(window.innerHeight - 260)
        return
      }
      setAlbumGridWidth(albumGridRef.current.clientWidth)
      setAlbumGridHeight(Math.max(420, window.innerHeight - 260))
    }

    updateGridSize()
    window.addEventListener('resize', updateGridSize)
    return () => window.removeEventListener('resize', updateGridSize)
  }, [])

  const renderPhotoCard = (photo: Photo): JSX.Element => (
    <div
      key={photo.id}
      onClick={() => openFullscreen(photo)}
      style={{
        position: 'relative',
        borderRadius: '12px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        backgroundColor: '#f0f0f0',
        aspectRatio: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.05)'
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.25)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      {photo.type === 'video' ? (
        <>
          <video
            src={photo.url}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '3rem'
          }}>
            ▶️
          </div>
        </>
      ) : (photo.type === 'pdf' || photo.url.toLowerCase().endsWith('.pdf')) ? (
        <>
          {photo.thumbnailUrl ? (
            <LazyImage
              src={photo.thumbnailUrl}
              alt={photo.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
              srcSet={photo.thumbnailSrcSet}
              webpSrc={photo.thumbnailWebpSrcSet}
              sizes="(max-width: 600px) 100vw, 300px"
            />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}>
              <div style={{
                width: '140px',
                height: '180px',
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '5px',
                display: 'flex',
                flexDirection: 'column',
                padding: '20px',
                gap: '10px',
                marginBottom: '20px'
              }}>
                <div style={{ width: '100%', height: '8px', backgroundColor: '#dc3545', borderRadius: '2px' }}></div>
                <div style={{ width: '80%', height: '8px', backgroundColor: '#e66b7a', borderRadius: '2px' }}></div>
                <div style={{ width: '90%', height: '8px', backgroundColor: '#e66b7a', borderRadius: '2px' }}></div>
              </div>
              <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>PDF</div>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', textAlign: 'center', maxWidth: '80%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {photo.title}
              </div>
            </div>
          )}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: photo.thumbnailUrl ? 'rgba(220, 53, 69, 0.2)' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '3rem',
            pointerEvents: 'none'
          }}>
            📄
          </div>
        </>
      ) : (
        <LazyImage
          src={photo.thumbnailUrl || photo.url}
          alt={photo.title}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          srcSet={photo.thumbnailSrcSet}
          webpSrc={photo.thumbnailWebpSrcSet}
          sizes="(max-width: 600px) 100vw, 300px"
        />
      )}
    </div>
  )

  const getAlbumPhotos = (albumId: string): Photo[] => {
    // Special case: if albumId is the virtual "All Photo View" album, return all photos
    // excluding photos from hidden albums: eqsong-images, images, thumbnails
    if (albumId === '__all_photos__') {
      const hiddenAlbumIds = ['eqsong-images', 'images', 'thumbnails']
      return photos.filter(photo => !hiddenAlbumIds.includes(photo.albumId))
    }
    return photos.filter(photo => photo.albumId === albumId)
  }

  const openFullscreen = (photo: Photo): void => {
    setSelectedPhoto(photo)
    setZoomLevel(100)
    setPanOffset({ x: 0, y: 0 })
    setFullscreenOpen(true)

    setOpenedAt(Date.now())
    try {
      const url = new URL(photo.url)
      // Decode the pathname and get the last part after the final /
      const decodedPath = decodeURIComponent(url.pathname)
      const filename = decodedPath.split('/').pop() || photo.title
      setOpenedPhoto({ albumId: photo.albumId, filename })
    } catch {
      setOpenedPhoto({ albumId: photo.albumId, filename: photo.title })
    }
  }

  const closeFullscreen = (): void => {
    setFullscreenOpen(false)
    setSelectedPhoto(null)
    setZoomLevel(100)
    setPanOffset({ x: 0, y: 0 })
    // Record review time/count for whichever photo or PDF was open
    if (openedAt && openedPhoto) {
      const seconds = Math.round((Date.now() - openedAt) / 1000)
      if (seconds > 0) {
        const encoded = encodeURIComponent(openedPhoto.filename)
        // Include auth token if available so backend can exclude admin reviews
        const token = localStorage.getItem('photoManagerToken')
        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
        fetch(`${BACKEND_URL}/api/photos/${openedPhoto.albumId}/${encoded}/review-time`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ seconds })
        }).then(async (resp) => {
          if (resp.ok) {
            const data = await resp.json()
            setReviewTimes(prev => ({ ...prev, [openedPhoto.filename]: data.total }))
            if (data.count) {
              setReviewCounts(prev => ({ ...prev, [openedPhoto.filename]: data.count }))
            }
          }
        }).catch(() => {})
      }
    }
    setOpenedAt(null)
    setOpenedPhoto(null)
  }

  // Add ESC key handler for fullscreen modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && fullscreenOpen) {
        closeFullscreen()
      }
    }
    
    if (fullscreenOpen) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [fullscreenOpen])

  const goToNextPhoto = (): void => {
    if (selectedPhoto && selectedAlbum) {
      const albumPhotos = getAlbumPhotos(selectedAlbum.id)
      const currentIndex = albumPhotos.findIndex(p => p.id === selectedPhoto.id)
      const nextIndex = (currentIndex + 1) % albumPhotos.length
      setSelectedPhoto(albumPhotos[nextIndex])
    }
  }

  const goToPrevPhoto = (): void => {
    if (selectedPhoto && selectedAlbum) {
      const albumPhotos = getAlbumPhotos(selectedAlbum.id)
      const currentIndex = albumPhotos.findIndex(p => p.id === selectedPhoto.id)
      const prevIndex = (currentIndex - 1 + albumPhotos.length) % albumPhotos.length
      setSelectedPhoto(albumPhotos[prevIndex])
    }
  }

  const currentMedia = heroMedia[currentMediaIndex]

  // If an album is selected, show photos view
  if (selectedAlbum) {
    const albumPhotos = getAlbumPhotos(selectedAlbum.id)
    
    return (
      <div className="home">
        <nav className="page-nav">
          <button 
            className="btn btn-secondary"
            onClick={() => { setSelectedAlbum(null); setSelectedPhoto(null) }}
          >
            ← Back to Gallery
          </button>
        </nav>

        <div className="page">
          <section className="page-header">
            <h1>{selectedAlbum.name}</h1>
            <p>{selectedAlbum.description}</p>
          </section>

          <section className="page-content">
            <div className="photos-header">
              <h2>Photos in this Album</h2>
              <div className="photo-count-display">Total: {albumPhotos.length} photos</div>
            </div>

            {albumPhotos.length > 0 ? (
              albumGridWidth > 0 && albumPhotos.length > 40 ? (() => {
                const gridColumns = Math.max(1, Math.floor(albumGridWidth / MIN_COLUMN_WIDTH))
                const gap = 20
                // Cards are square (aspectRatio: 1), so their rendered height equals their
                // computed column width - derive the row height from that instead of a fixed
                // constant, otherwise cards taller than ROW_HEIGHT get clipped by overflow:hidden
                const columnWidth = (albumGridWidth - (gridColumns - 1) * gap) / gridColumns
                const rowHeight = Math.max(ROW_HEIGHT, Math.ceil(columnWidth) + gap)
                return (
                <div ref={albumGridRef} style={{ width: '100%', height: Math.min(albumGridHeight || 760, 760), overflow: 'hidden' }}>
                  <FixedSizeList
                    height={Math.min(albumGridHeight || 760, 760)}
                    width={albumGridWidth}
                    itemCount={Math.ceil(albumPhotos.length / gridColumns)}
                    itemSize={rowHeight}
                    overscanCount={3}
                  >
                    {({ index, style }) => {
                      const columns = gridColumns
                      const rowPhotos = albumPhotos.slice(index * columns, index * columns + columns)
                      return (
                        <div
                          style={{
                            ...style,
                            display: 'grid',
                            // Always use the full column count (not rowPhotos.length) so a
                            // partial last row doesn't stretch cards wider/taller than ROW_HEIGHT
                            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                            gap: '20px',
                            padding: '0 0 20px 0'
                          }}
                        >
                          {rowPhotos.map(photo => renderPhotoCard(photo))}
                        </div>
                      )
                    }}
                  </FixedSizeList>
                </div>
                )
              })() : (
                <div ref={albumGridRef} style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '20px',
                  padding: '20px 0'
                }}>
                  {albumPhotos.map(photo => renderPhotoCard(photo))}
                </div>
              )
            ) : (
              <div className="empty-state">
                <p>No photos in this album yet.</p>
              </div>
            )}
          </section>
        </div>

        {/* Fullscreen Modal */}
        {fullscreenOpen && selectedPhoto && (
          <div 
            className="fullscreen-overlay" 
            onClick={closeFullscreen}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              transition: 'background-color 0.3s ease'
            }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 20px',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              zIndex: 2001
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', color: 'white', marginBottom: '4px' }}>
                  {selectedPhoto.title}
                </h3>
                <p style={{ margin: 0, fontSize: '12px', color: '#aaa' }}>
                  Zoom: {Math.round(zoomLevel)}% • Scroll wheel to zoom • Drag to pan
                </p>
              </div>
              <button 
                onClick={closeFullscreen}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  transition: 'all 0.2s',
                  zIndex: 2002
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#c0392b')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#e74c3c')}
                title="Close (ESC)"
              >
                ✕
              </button>
            </div>

            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'relative',
                width: '100%',
                height: 'calc(100vh - 140px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                cursor: zoomLevel > 100 ? (isDragging ? 'grabbing' : 'grab') : 'default'
              }}
              onWheel={(e) => {
                const isPdf = selectedPhoto.type === 'pdf' || selectedPhoto.url.toLowerCase().endsWith('.pdf')
                if (isPdf) return
                e.preventDefault()
                const delta = e.deltaY > 0 ? -10 : 10
                setZoomLevel(prev => Math.max(100, Math.min(300, prev + delta)))
              }}
              onMouseDown={(e) => {
                if (zoomLevel > 100) {
                  setIsDragging(true)
                  setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y })
                }
              }}
              onMouseMove={(e) => {
                if (isDragging && zoomLevel > 100) {
                  const newX = e.clientX - dragStart.x
                  const newY = e.clientY - dragStart.y
                  setPanOffset({ x: newX, y: newY })
                }
              }}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
            >
              {(() => {
                const isPdf = selectedPhoto.type === 'pdf' || selectedPhoto.url.toLowerCase().endsWith('.pdf')
                const isVideo = selectedPhoto.type === 'video'

                if (isVideo) {
                  return (
                    <video
                      src={selectedPhoto.url}
                      controls
                      autoPlay
                      style={{
                        width: '90%',
                        height: '90%',
                        objectFit: 'contain'
                      }}
                    />
                  )
                }

                if (isPdf) {
                  return (
                    <div style={{ width: '100%', height: '100%', overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
                      <PDFViewer
                        url={selectedPhoto.url}
                        title={selectedPhoto.title || 'PDF Document'}
                        isFullscreen={false}
                      />
                    </div>
                  )
                }

                return (
                  <LazyImage
                    src={selectedPhoto.url}
                    alt={selectedPhoto.title}
                    style={{
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '90%',
                      maxHeight: '90%',
                      objectFit: 'contain',
                      transform: `scale(${zoomLevel / 100}) translate(${panOffset.x / (zoomLevel / 100)}px, ${panOffset.y / (zoomLevel / 100)}px)`,
                      transformOrigin: 'center center',
                      transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                      userSelect: 'none'
                    }}
                    srcSet={selectedPhoto.thumbnailSrcSet}
                    webpSrc={selectedPhoto.thumbnailWebpSrcSet}
                    sizes="90vw"
                  />
                )
              })()}

              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevPhoto()
                }}
                style={{
                  position: 'absolute',
                  left: '20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '32px',
                  cursor: 'pointer',
                  background: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 15px',
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                  zIndex: 2001
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
                }}
                title="Previous (←)"
              >
                ❮
              </button>

              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToNextPhoto()
                }}
                style={{
                  position: 'absolute',
                  right: '20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '32px',
                  cursor: 'pointer',
                  background: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 15px',
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                  zIndex: 2001
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
                }}
                title="Next (→)"
              >
                ❯
              </button>
            </div>

            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '16px 20px',
              maxHeight: '80px',
              overflow: 'auto',
              zIndex: 2001
            }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', color: 'white' }}>{selectedPhoto.title}</h2>
              <p style={{ margin: '0 0 8px 0', color: '#ccc', fontSize: '14px' }}>{selectedPhoto.caption}</p>
              <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Uploaded: {new Date(selectedPhoto.uploadDate).toLocaleDateString()}</p>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Main home page view
  return (
    <div className="home">
      {/* Hero Header with Animated Media */}
      <section className="hero-section" style={{ height: `${heroHeight}px` }}>
        {heroMedia.length > 0 ? (
          <>
            {currentMedia?.type === 'video' ? (
              <video
                src={currentMedia?.url}
                autoPlay
                muted
                loop
                className="hero-image"
                style={{ objectFit: 'cover', objectPosition: currentMedia?.focalPoint || 'center top', width: '100%', height: '100%' }}
              />
            ) : (
              <LazyImage
                src={currentMedia?.url || ''}
                alt="Hero"
                className="hero-image"
                style={{ objectFit: 'cover', objectPosition: currentMedia?.focalPoint || 'center top' }}
              />
            )}
          </>
        ) : (
          <LazyImage
            src="https://bird-created.s3.us-east-1.amazonaws.com/eqsong-images/_DSC2154-Recovered+copy.jpg"
            alt="Hero"
            className="hero-image"
            style={{ objectFit: 'cover', objectPosition: 'center top' }}
          />
        )}
        <div className="hero-overlay">
          <div style={{ position: 'absolute', top: '20px', right: '20px', display: 'flex', gap: '15px', zIndex: 10 }}>
            <a 
              href="https://www.flickr.com/people/eqsong" 
              target="_blank" 
              rel="noopener noreferrer"
              title="Visit my Flickr profile"
              style={{ fontSize: '24px', textDecoration: 'none', color: 'white', transition: 'transform 0.2s' }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.2)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              📸
            </a>
            <a 
              href="https://www.facebook.com/qing.song.562/" 
              target="_blank" 
              rel="noopener noreferrer"
              title="Visit my Facebook profile"
              style={{ fontSize: '24px', textDecoration: 'none', color: 'white', transition: 'transform 0.2s' }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.2)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              f
            </a>
          </div>
          <h1 className="hero-title">Welcome to My Retirement Journey</h1>
          <p className="hero-subtitle">Exploring AI research, hobbies & photography through sports, birding, travel, and an academic lens</p>
          <div className="hero-cta">
            <a href="#blogs" className="btn btn-primary">📝 Featured Blogs</a>
            <a href="https://style-transfer.eqsong.com" target="_blank" rel="noopener noreferrer" className="btn btn-primary">🎨 Image Style Transfer</a>
          </div>
        </div>
        {heroMedia.length > 1 && (
          <div className="hero-indicators">
            {heroMedia.map((_, index) => (
              <div
                key={index}
                className="indicator"
                style={{
                  opacity: index === currentMediaIndex ? 1 : 0.5,
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setCurrentMediaIndex(index)
                }}
              />
            ))}
          </div>
        )}
      </section>

      {/* Quick Stats */}
      <section className="stats-section">
        <Link to="/academics#publications" className="stat-item" style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}>
          <div className="stat-number">52</div>
          <div className="stat-label">Refereed Academic Journal Publications</div>
        </Link>
        <div className="stat-item" style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }} onClick={() => document.getElementById('gallery-section')?.scrollIntoView({ behavior: 'smooth' })}>
          <div className="stat-number">{albums.filter(a => !a.hidden).length + 1}</div>
          <div className="stat-label">Photo Albums</div>
        </div>
        <div className="stat-item" style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }} onClick={() => document.getElementById('gallery-section')?.scrollIntoView({ behavior: 'smooth' })}>
          <div className="stat-number">{photos.filter(photo => !['eqsong-images', 'images', 'thumbnails'].includes(photo.albumId)).length}+</div>
          <div className="stat-label">Photos</div>
        </div>
        <Link to="/academics" className="stat-item" style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}>
          <div className="stat-number">30+</div>
          <div className="stat-label">Years Academic Experience</div>
        </Link>
      </section>

      {/* Photo Gallery Section */}
      <section id="gallery-section" className="gallery-section" style={{ padding: '60px 20px', backgroundColor: '#f8f9fa' }}>
        <div className="section-header">
          <h2>📸 Photo Gallery</h2>
          <p>Explore my photography collection - birding, travel, sports, and more</p>
        </div>
        
        <div className="albums-grid" style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '24px',
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '20px 0'
        }}>
          {albums && albums.length > 0 ? (
            <>
              {/* Regular albums */}
              {albums.filter(a => !a.hidden).map((album) => (
                <div key={album.id} className="album-card">
                  <div className="album-cover-image">
                        {album.coverImage && album.coverImage.toLowerCase().endsWith('.pdf') ? (
                          <div style={{
                            width: '100%',
                            height: '180px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                            color: 'white',
                            borderRadius: '8px'
                          }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '48px' }}>📄</div>
                              <div style={{ fontSize: '14px', marginTop: '6px' }}>{album.name}</div>
                            </div>
                          </div>
                        ) : (
                          <LazyImage src={album.coverImage} alt={album.name} />
                        )}
                      </div>
                  <div className="album-info">
                    <h3>{album.name}</h3>
                    <p className="album-description">{album.description}</p>
                    <div className="album-meta">
                      <span className="photo-count">📸 {album.photoCount} photos</span>
                      <span className="album-date">{new Date(album.createdDate).toLocaleDateString()}</span>
                    </div>
                    <button
                      className="btn btn-primary btn-full"
                      onClick={() => setSelectedAlbum(album)}
                    >
                      View Album
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Virtual "All Photo View" album - always last */}
              {photos.length > 0 && (() => {
                const allPhotosAlbum = createAllPhotosAlbum()
                return (
                  <div key={allPhotosAlbum.id} className="album-card" style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                  }}>
                    <div className="album-cover-image">
                      {allPhotosAlbum.coverImage && allPhotosAlbum.coverImage.toLowerCase().endsWith('.pdf') ? (
                        <div style={{
                          width: '100%',
                          height: '180px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                          color: 'white',
                          borderRadius: '8px'
                        }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '48px' }}>📄</div>
                            <div style={{ fontSize: '14px', marginTop: '6px' }}>{allPhotosAlbum.name}</div>
                          </div>
                        </div>
                      ) : (
                        <LazyImage src={allPhotosAlbum.coverImage} alt={allPhotosAlbum.name} />
                      )}
                    </div>
                    <div className="album-info">
                      <h3 style={{ color: 'white' }}>{allPhotosAlbum.name}</h3>
                      <p className="album-description" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {allPhotosAlbum.description}
                      </p>
                      <div className="album-meta">
                        <span className="photo-count" style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                          📸 {allPhotosAlbum.photoCount} photos
                        </span>
                        <span className="album-date" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          From {albums.filter(a => !a.hidden).length} albums
                        </span>
                      </div>
                      <button
                        className="btn btn-primary btn-full"
                        onClick={() => setSelectedAlbum(allPhotosAlbum)}
                        style={{
                          background: 'white',
                          color: '#667eea',
                          border: 'none'
                        }}
                      >
                        View All Photos
                      </button>
                    </div>
                  </div>
                )
              })()}
            </>
          ) : (
            <p>Loading albums...</p>
          )}
        </div>
      </section>

      {/* Featured Blogs */}
      <section id="blogs" className="featured-blogs">
        <div className="section-header">
          <h2>📝 Featured Blogs</h2>
          <p>Insights on AI, birding adventures, and photography techniques</p>
        </div>
        <div className="blogs-grid">
          <Link to="/blog/ai" className="blog-card">
            <LazyImage src="https://bird-created.s3.us-east-1.amazonaws.com/eqsong-images/blog-cover-ai.jpg" alt="AI Blog" />
            <div className="blog-content">
              <span className="blog-category">AI Research</span>
              <h3>AI Fundamentals - Part I</h3>
              <p>Exploring linear classification and the foundations of machine learning algorithms.</p>
              <span className="read-more">Read More →</span>
            </div>
          </Link>

          <Link to="/blog/ai2" className="blog-card">
            <LazyImage src="https://cdn.eqsong.com/eqsong-images/Figure-1.jpg" alt="AI Blog Part II" />
            <div className="blog-content">
              <span className="blog-category">AI Research</span>
              <h3>AI Fundamentals - Part II</h3>
              <p>Understanding nonlinear classification with neural networks and the XOR problem.</p>
              <span className="read-more">Read More →</span>
            </div>
          </Link>

          <Link to="/blog/birding" className="blog-card">
            <LazyImage src="https://bird-created.s3.us-east-1.amazonaws.com/eqsong-images/blog-cover-woodpecker.jpg" alt="Birding" />
            <div className="blog-content">
              <span className="blog-category">Birding</span>
              <h3>Red-headed Woodpecker</h3>
              <p>Capturing the incredible moment of a woodpecker catching an insect mid-flight.</p>
              <span className="read-more">Read More →</span>
            </div>
          </Link>

          <Link to="/blog/eagles" className="blog-card">
            <LazyImage src="https://bird-created.s3.us-east-1.amazonaws.com/eqsong-images/blog-cover-eagles.jpg" alt="Photography" />
            <div className="blog-content">
              <span className="blog-category">Photography Trip</span>
              <h3>Conowingo Dam Eagles</h3>
              <p>An unforgettable eagle photography trip to Conowingo Dam in November 2023.</p>
              <span className="read-more">Read More →</span>
            </div>
          </Link>
        </div>
      </section>

    </div>
  )
}

export default Home
